import { DialogComponent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';

/** Section Rendering API handle (not mounted in the theme editor). */
const CART_DRAWER_CROSS_SELL_SECTION_ID = 'cart-drawer-cross-sell';

/**
 * @returns {string | undefined}
 */
function readCartFirstProductId(root) {
  const el = root.querySelector('[data-cart-first-product-id]');
  const id = el?.getAttribute('data-cart-first-product-id');
  return id && id !== '' ? id : undefined;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {string | undefined} */
  #lastRecommendationsProductId;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartEvent);
    this.#openFromUrlIfRequested();
  }

  /** Open drawer when landing from /cart redirect (?open_cart=1). */
  #openFromUrlIfRequested() {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (!params.has('open_cart')) return;

    params.delete('open_cart');
    const nextUrl =
      window.location.pathname +
      (params.toString() ? `?${params.toString()}` : '') +
      window.location.hash;
    window.history.replaceState({}, '', nextUrl);

    requestAnimationFrame(() => {
      this.open();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartEvent);
  }

  /**
   * Cart add/update share one event name; handle auto-open and recommendations sync once.
   */
  #handleCartEvent = () => {
    if (this.hasAttribute('auto-open') && !this.querySelector('dialog[open]')) {
      this.open();
      return;
    }

    this.#scheduleRecommendationsSync();
  };

  /** After cart morph, wait for DOM to settle before syncing recommendations. */
  #scheduleRecommendationsSync() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#syncCartDrawerRecommendations();
      });
    });
  }

  /**
   * @param {HTMLElement} pr
   */
  #requestDrawerRecommendationsLoad(pr) {
    if (pr.dataset.recommendationsPerformed === 'true') return;
    if (typeof pr.loadCartDrawerRecommendations === 'function') {
      pr.loadCartDrawerRecommendations();
    }
  }

  #syncCartDrawerRecommendations() {
    const pr = this.querySelector('product-recommendations#cart-drawer-you-may-like');
    if (!(pr instanceof HTMLElement)) return;

    if (pr.dataset.sectionId !== CART_DRAWER_CROSS_SELL_SECTION_ID) {
      pr.dataset.sectionId = CART_DRAWER_CROSS_SELL_SECTION_ID;
    }

    // Recover from transient load errors that put the element in a hidden state.
    if (pr.classList.contains('hidden')) {
      pr.classList.remove('hidden');
      delete pr.dataset.error;
    }

    const firstProductId = readCartFirstProductId(this);
    const productIdChanged =
      Boolean(firstProductId) &&
      Boolean(this.#lastRecommendationsProductId) &&
      firstProductId !== this.#lastRecommendationsProductId;

    if (firstProductId && pr.dataset.productId !== firstProductId) {
      pr.dataset.productId = firstProductId;
    }

    if (productIdChanged) {
      delete pr.dataset.recommendationsPerformed;
      delete pr.dataset.error;
      pr.classList.remove('hidden');
    }

    if (firstProductId) {
      this.#lastRecommendationsProductId = firstProductId;
    }

    const dialogOpen = Boolean(this.querySelector('dialog[open]'));
    if (dialogOpen) {
      this.#requestDrawerRecommendationsLoad(pr);
    }
  }

  open() {
    this.#syncCartDrawerRecommendations();
    this.showDialog();

    /** IntersectionObserver rarely fires inside <dialog>; retry load after paint. */
    this.#scheduleRecommendationsSync();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
